import AgentConversationsAPI from '../../api/agentConversations';
import { throwErrorMessage } from '../utils/api';

const state = {
  records: [],
  uiFlags: {
    fetchingList: false,
    creatingItem: false,
  },
};

const getters = {
  getConversations: _state =>
    [..._state.records].sort((a, b) => b.updated_at - a.updated_at),
  getConversationById: _state => id => _state.records.find(c => c.id === id),
  getUnreadTotal: _state =>
    _state.records.reduce((sum, c) => sum + (c.unread_count || 0), 0),
  getUIFlags: _state => _state.uiFlags,
};

const mutations = {
  SET_CONVERSATIONS(_state, records) {
    _state.records = records;
  },
  SET_UI_FLAG(_state, flags) {
    _state.uiFlags = { ..._state.uiFlags, ...flags };
  },
  UPSERT_CONVERSATION(_state, record) {
    const idx = _state.records.findIndex(c => c.id === record.id);
    if (idx >= 0) {
      _state.records.splice(idx, 1, record);
    } else {
      _state.records.unshift(record);
    }
  },
  UPDATE_LAST_MESSAGE(_state, { conversationId, lastMessage }) {
    const conv = _state.records.find(c => c.id === conversationId);
    if (conv) {
      conv.last_message = lastMessage;
      conv.updated_at = lastMessage.created_at;
    }
  },
  INCREMENT_UNREAD(_state, conversationId) {
    const conv = _state.records.find(c => c.id === conversationId);
    if (conv) conv.unread_count = (conv.unread_count || 0) + 1;
  },
  CLEAR_UNREAD(_state, conversationId) {
    const conv = _state.records.find(c => c.id === conversationId);
    if (conv) conv.unread_count = 0;
  },
};

const actions = {
  fetch: async ({ commit }) => {
    commit('SET_UI_FLAG', { fetchingList: true });
    try {
      const { data } = await AgentConversationsAPI.getAll();
      commit('SET_CONVERSATIONS', data.payload || []);
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit('SET_UI_FLAG', { fetchingList: false });
    }
  },

  findOrCreate: async ({ commit }, participantId) => {
    commit('SET_UI_FLAG', { creatingItem: true });
    try {
      const { data } = await AgentConversationsAPI.findOrCreate(participantId);
      commit('UPSERT_CONVERSATION', data);
      return data;
    } catch (error) {
      throwErrorMessage(error);
      throw error;
    } finally {
      commit('SET_UI_FLAG', { creatingItem: false });
    }
  },

  receiveMessage: async (context, messageData) => {
    const { commit, rootGetters } = context;
    const currentUserId = rootGetters.getCurrentUser?.id;
    const { agent_conversation_id: convId } = messageData;

    const exists = context.state.records.some(c => c.id === convId);
    if (!exists) {
      // First message of a new conversation — fetch full object and insert into list
      try {
        const { data } = await AgentConversationsAPI.getById(convId);
        commit('UPSERT_CONVERSATION', data);
      } catch {
        // silent: conversation will appear on next page load
      }
      return;
    }

    // Conversation already in store — optimistic update without round-trip
    commit('UPDATE_LAST_MESSAGE', {
      conversationId: convId,
      lastMessage: {
        content: messageData.content,
        created_at: messageData.created_at,
        sender_id: messageData.sender.id,
      },
    });
    if (messageData.sender.id !== currentUserId) {
      commit('INCREMENT_UNREAD', convId);
    }
  },

  clearUnread: ({ commit }, conversationId) => {
    commit('CLEAR_UNREAD', conversationId);
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
